import React, { useRef, useState } from 'react';
import { styled } from '@tmap/mmm-style-guide/src/styled';

const generateDrawerTransform = (offset, expanded) => {
  if (!expanded && offset === 0) return 'translateY(100%) translateY(-20px)';
  if (offset < 0) return `scaleY(${1 + (Math.log(1 - offset) / 50)})`;
  return `translateY(${offset}px)`;
};

const DrawerWrapper = styled('div', {
  shouldForwardProp: (p) => p !== 'breakpoint',
})(({ theme, breakpoint }) => ({
  display: 'contents',
  [theme.breakpoints.down(breakpoint)]: {
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    zIndex: 99,
    background: theme.palette.common.white,
    boxShadow: theme.shadows[20],
    margin: 0,
    padding: theme.spacing(3, 3, 2, 3),
    borderTopRightRadius: '12px',
    borderTopLeftRadius: '12px',
    // cursors
    cursor: 'ns-resize',
    '&>*': {
      cursor: 'initial',
    },
    // drag animation handling
    transformOrigin: 'bottom',
    transition: 'transform 0.05s linear',
    // disable dragging scrolling the page
    touchAction: 'none',
    // extra draggable hitbox size
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-16px',
      width: '100%',
      height: '32px',
    },
    // drag indicator
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '6px',
      width: '25%',
      borderRadius: '8px',
      height: '4px',
      background: theme.palette.grey[300],
    },
    '&.hidden': {
      transform: 'translateY(100%) !important',
      transition: 'transform 0.10s linear',
    },
  },
}));

function ResponsiveBottomDrawer(props) {
  const {
    breakpoint = 'md',
    children,
    isHidden = false,
  } = props;
  const [dragStart, setDragStart] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const draggableRef = useRef();

  const startDrag = (e) => {
    if (draggableRef.current && e.target === draggableRef.current) {
      const here = e?.changedTouches?.[0]?.screenY || 0;
      setDragStart(here);
    }
  };
  const handleDrag = (e) => {
    if (draggableRef.current && dragStart) {
      const here = e?.changedTouches?.[0]?.screenY || 0;
      const dragDistance = here - dragStart;
      const elementOffset = expanded ? 0 : draggableRef.current.clientHeight - 20;
      draggableRef.current.style.transform = generateDrawerTransform(
        elementOffset + dragDistance,
        expanded,
      );
    }
  };
  const stopDrag = (e) => {
    if (draggableRef.current && dragStart) {
      const here = e?.changedTouches?.[0]?.screenY || 0;
      const dragDistance = here - dragStart;
      // set expand state based on whether or not you dragged up or down
      const expand = dragDistance < 0;
      draggableRef.current.style.transform = generateDrawerTransform(0, expand);
      setExpanded(expand);
      setDragStart(null);
    }
  };

  const handleClick = (e) => {
    if (draggableRef.current && e.target === draggableRef.current) {
      draggableRef.current.style.transform = generateDrawerTransform(0, !expanded);
      setExpanded(!expanded);
    }
  };

  return (
    <DrawerWrapper
      onTouchStart={startDrag}
      onTouchMove={handleDrag}
      onTouchEnd={stopDrag}
      onTouchCancel={stopDrag}
      onMouseDown={handleClick}
      breakpoint={breakpoint}
      ref={draggableRef}
      className={isHidden ? 'hidden' : ''}
      aria-hidden={isHidden}
    >
      {children}
    </DrawerWrapper>
  );
}

export default ResponsiveBottomDrawer;
